import { SmileOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Button, Popover, PopoverProps, Tabs } from 'antd';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { packList, emojiInfoMap, EmojiInfo, EmojiChar } from './assets/emoji';
import { ReactEditor } from 'slate-react';
import { Transforms } from 'slate';

const PopoverContent = styled.div`
  width: 334px;
  max-height: 420px;
  margin: -12px -16px;
  padding: 0 4px;
  overflow: auto;
`;

export interface EmojiPickerProps extends PopoverProps {
  editor: ReactEditor;
}

const EmojiPicker = memo((props: EmojiPickerProps) => {
  const { editor, ...otherProps } = props;

  const [visible, setVisible] = useState(false);

  const selectionRef = useRef(editor.selection);

  useEffect(() => {
    if (visible) {
      selectionRef.current = editor.selection;
    }
  }, [visible, editor]);

  const onPick = useCallback(
    ({ emoji }: EmojiInfo) => {
      Transforms.insertText(editor, emoji);
      setVisible(false);
    },
    [editor],
  );

  return (
    <Popover
      visible={visible}
      onVisibleChange={setVisible}
      content={
        <PopoverContent>
          <EmojiPackList packList={packList} onPick={onPick} />
        </PopoverContent>
      }
      {...otherProps}
    >
      <Button type="text" icon={<SmileOutlined />} />
    </Popover>
  );
});

const PackListWrapper = styled.div`
  position: relative;
  > .ant-tabs {
    position: sticky;
    top: 0;
    padding-left: 8px;
    background: var(--fett-bg-panel);
    z-index: 1;
    > .ant-tabs-nav {
      margin-bottom: 0;
    }
  }
`;

interface EmojiPackListProps {
  packList: EmojiChar[][];
  onPick: (emojiInfo: EmojiInfo) => void;
}

const EmojiPackList = memo((props: EmojiPackListProps) => {
  const { packList, onPick } = props;

  const packMap = useMemo(() => {
    return packList
      .filter((pack) => pack.length > 0)
      .reduce<Partial<Record<EmojiChar, EmojiChar[]>>>((res, cur) => {
        res[cur[0]] = cur;

        return res;
      }, {});
  }, [packList]);

  const [activeKey, setActiveKey] = useState(() => Object.keys(packMap)[0]);

  const tabs = useMemo(() => {
    return (
      <Tabs activeKey={activeKey} onChange={(activeKey: any) => setActiveKey(activeKey)}>
        {Object.keys(packMap).map((key) => {
          const { [key]: pack } = packMap;

          if (!pack) {
            return null;
          }

          const {
            [key]: { backgroundImage, backgroundPosition },
          } = emojiInfoMap;

          return (
            <Tabs.TabPane
              key={key}
              tab={
                <Emoji
                  size={24}
                  backgroundImage={backgroundImage}
                  backgroundPosition={backgroundPosition}
                />
              }
            />
          );
        })}
      </Tabs>
    );
  }, [packMap, activeKey]);

  const { [activeKey]: activePack = [] } = packMap;

  return (
    <PackListWrapper>
      {tabs}
      <EmojiPack pack={activePack} onPick={onPick} />
    </PackListWrapper>
  );
});

const EmojiPackWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  > button {
    padding: 0;
    margin: 7px;
  }
`;

interface EmojiPackProps {
  pack: EmojiChar[];
  onPick: (emojiInfo: EmojiInfo) => void;
}

const EmojiPack = memo((props: EmojiPackProps) => {
  const { pack, onPick } = props;

  return (
    <EmojiPackWrapper>
      {pack.map((emoji) => {
        const { [emoji]: info } = emojiInfoMap;

        const { backgroundImage, backgroundPosition } = info;

        return (
          <Button size="small" type="text">
            <Emoji
              key={emoji}
              title={emoji}
              size={24}
              backgroundImage={backgroundImage}
              backgroundPosition={backgroundPosition}
              onClick={(e) => {
                e.preventDefault();

                onPick(info);
              }}
            />
          </Button>
        );
      })}
    </EmojiPackWrapper>
  );
});

export default EmojiPicker;

const Emoji = styled.div<{
  size: number;
  backgroundImage: string;
  backgroundPosition: readonly [number, number];
}>`
  width: ${({ size }) => `${size}px`};
  height: ${({ size }) => `${size}px`};
  background-size: ${({ size }) => `${(200 * size) / 40}px ${(200 * size) / 40}px`};
  background-image: ${({ backgroundImage }) => `url(${backgroundImage})`};
  background-position: ${({ size, backgroundPosition: [x, y] }) => `${x * size}px ${y * size}px`};
`;
